import { ExternalLink } from "@/components/ExternalLink";
import i18n from "@/i18n";
import { formatNumber } from "@/features/Table/utils";
import { parseDate } from "@/lib/utils";
import { Link } from "@heroui/link";
import { ColumnDef } from "@tanstack/react-table";
import { toWorkspacePath } from "@/features/Workspaces/workspaceRoutes";
import { Tables } from "@/gen/tableNames/tables";
import { createEnum } from "@/lib/utils/createEnum";

export type N1Transaction = {
  id: number;
  date: Date;
  n1_portfolio_client?: string;
  n1_portfolio_id?: number;
  n1_portfolio_name?: string;
  n1_static_datum_id?: number;
  n1_static_datum_name?: string;
  n1_portfolio_reporting_currency?: string;
  quantity?: number;
  market_value_ptf_ccy?: number;
  ptf_currency?: string;
  market_value_local_ccy?: number;
  local_currency?: string;
  category?: string;
  transfer?: string;
  description?: string;
  fees?: number;
  price_local_ccy?: number;
  tax?: number;
  custodian_broker_fee?: number;
  broker_fee?: number;
  other_fee?: number;
  fee_currency?: string;
  fee_type?: string;
};
const n1TransactionFields = createEnum<N1Transaction>();

const t = (key: string, defaultValue: string) =>
  i18n.t(`${Tables.N1_TRANSACTIONS}.columns.${key}`, {
    ns: "tables",
    defaultValue,
  });

export const porfolios_columns: ColumnDef<N1Transaction>[] = [
  {
    accessorKey: n1TransactionFields.id,
    header: t("id", "ID"),
  },
  {
    accessorKey: n1TransactionFields.date,
    header: t("date", "Date"),
    cell: ({ row }) => {
      return (
        <div className="whitespace-nowrap">
          {parseDate(String(row.getValue(n1TransactionFields.date)))}
        </div>
      );
    },
  },
  {
    accessorKey: n1TransactionFields.n1_portfolio_client,
    header: "Client",
  },
  {
    accessorKey: n1TransactionFields.n1_portfolio_name,
    header: t("n1_portfolio_name", "Account"),
    cell: ({ row }) => {
      return (
        <ExternalLink
          href={toWorkspacePath(
            `/admin/n1/n1_portfolios/${row.original.n1_portfolio_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.getValue(n1TransactionFields.n1_portfolio_name)}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: n1TransactionFields.n1_static_datum_name,
    header: t("n1_static_datum_name", "Asset"),
    cell: ({ row }) => {
      return (
        <ExternalLink
          href={toWorkspacePath(
            `/admin/n1/n1_static_data/${row.original.n1_static_datum_id}`,
          )}
          className="text-xs"
          underline="hover"
        >
          {row.getValue(n1TransactionFields.n1_static_datum_name)}
        </ExternalLink>
      );
    },
  },
  {
    accessorKey: n1TransactionFields.quantity,
    header: () => <div className="text-right">{t("quantity", "Quantity")}</div>,
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue(n1TransactionFields.quantity))}
        </div>
      );
    },
  },
  {
    accessorKey: n1TransactionFields.market_value_ptf_ccy,
    header: () => (
      <div className="text-right">
        {t("market_value_ptf_ccy", "Market value (reporting ccy)")}
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue(n1TransactionFields.market_value_ptf_ccy))}
        </div>
      );
    },
  },
  {
    header: t(
      "n1_portfolio_reporting_currency",
      "Portfolio reporting currency",
    ),
    accessorKey: n1TransactionFields.n1_portfolio_reporting_currency,
  },
  {
    accessorKey: n1TransactionFields.market_value_local_ccy,
    header: () => (
      <div className="text-right">
        {t("market_value_local_ccy", "Market value local ccy")}
      </div>
    ),
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(
            row.getValue(n1TransactionFields.market_value_local_ccy),
          )}
        </div>
      );
    },
  },
  {
    accessorKey: n1TransactionFields.local_currency,
    header: t("local_currency", "Local currency"),
  },
  {
    accessorKey: n1TransactionFields.fees,
    header: () => <div className="text-right">{t("fees", "Fees")}</div>,
    cell: ({ row }) => {
      return (
        <div className="text-right">
          {formatNumber(row.getValue(n1TransactionFields.fees))}
        </div>
      );
    },
  },
  {
    accessorKey: n1TransactionFields.fee_currency,
    header: t("fee_currency", "Fee currency"),
  },
  {
    accessorKey: n1TransactionFields.description,
    header: t("description", "Description"),
  },
  {
    accessorKey: n1TransactionFields.category,
    header: t("category", "Movement type"),
  },
  {
    accessorKey: n1TransactionFields.transfer,
    header: t("transfer", "Transfer type"),
    cell: ({ row }) => {
      return (
        <div className="text-left">
          {row.getValue(n1TransactionFields.transfer)}
        </div>
      );
    },
  },
  {
    id: "actions",
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Link
            href={`${window.location.pathname}/${row.getValue(n1TransactionFields.id)}`}
            className="text-xs"
            underline="hover"
          >
            {i18n.t("actions.edit")}
          </Link>
        </div>
      );
    },
  },
];

// export const porfolios_audit_columns = getAuditColumns<N1Transaction>(porfolios_columns)
